import { useMemo } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { cn } from '@/shared/utils/cn';
import { hasCustomizationOptions } from '@/features/customization/utils/parseCustomizationOptions';
import { formatPdpCarat } from '@/features/pdp/utils/formatPdpCarat';

interface ProductSpecsProps {
  designName: string;
  collectionName: string | null;
  collectionPageUrl: string | null;
  metal: string;
  metalType: string;
  goldPurity: string | null;
  metalWeight: number;
  diamondCarat: number | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  shortDescLine: string | null;
  customizationOptions: unknown | null;
  onCustomize?: () => void;
  onViewMoreDetailsClick?: () => void;
  onCustomizeClick?: () => void;
}

interface StoneSpec {
  label: string;
  type: string | null;
  carat: number | null;
}

function getMetalLabel(metalType: string): string {
  return metalType === 'Gold' ? 'Gold & KT' : metalType;
}

function parseDiamondStoneType(diamondStoneType: string | null): string | null {
  if (!diamondStoneType) return null;
  const clarity = diamondStoneType.slice(0, -2);
  const color = diamondStoneType.slice(-2);
  return `${clarity} ${color}`.trim();
}

function getStoneSpec(
  solitaireClarity: string | null,
  solitaireColor: string | null,
  solitaireCarat: number | null,
  diamondStoneType: string | null,
  diamondCarat: number | null
): StoneSpec | null {
  if (solitaireClarity || solitaireCarat) {
    const type =
      solitaireClarity && solitaireColor
        ? `${solitaireClarity} ${solitaireColor}`
        : solitaireClarity ?? solitaireColor;
    return {
      label: 'Solitaire',
      type,
      carat: solitaireCarat,
    };
  }

  if (diamondStoneType || diamondCarat) {
    return {
      label: 'Diamond',
      type: parseDiamondStoneType(diamondStoneType),
      carat: diamondCarat,
    };
  }

  return null;
}

function formatCarat(carat: number): string {
  return `${formatPdpCarat(carat)} CT`;
}

function formatWeight(weight: number): string {
  return `${weight} Grams`;
}

function SpecPill({
  children,
  uppercase = false,
}: {
  children: string;
  uppercase?: boolean;
}) {
  return (
    <span
      className={cn(
        'm-[3px] inline-block whitespace-nowrap rounded-[3.7px] border border-[#d9d9d9] bg-[rgba(233,233,233,0.25)] px-1.5 py-[3px] text-xs font-bold leading-snug text-black',
        uppercase && 'uppercase'
      )}
    >
      {children}
    </span>
  );
}

export function ProductSpecs({
  designName,
  collectionName,
  collectionPageUrl,
  metal,
  metalType,
  goldPurity,
  metalWeight,
  diamondCarat,
  diamondStoneType,
  solitaireCarat,
  solitaireClarity,
  solitaireColor,
  shortDescLine,
  customizationOptions,
  onCustomize,
  onViewMoreDetailsClick,
  onCustomizeClick,
}: ProductSpecsProps) {
  const stoneSpec = useMemo(
    () =>
      getStoneSpec(
        solitaireClarity,
        solitaireColor,
        solitaireCarat,
        diamondStoneType,
        diamondCarat
      ),
    [
      solitaireClarity,
      solitaireColor,
      solitaireCarat,
      diamondStoneType,
      diamondCarat,
    ]
  );

  const showCustomization = hasCustomizationOptions(customizationOptions);
  const metalLabel = getMetalLabel(metalType);

  const handleViewMoreDetails = () => {
    onViewMoreDetailsClick?.();
    const target =
      document.getElementById('about-product') ??
      document.getElementById('product-details');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCustomizeClick = () => {
    onCustomizeClick?.();
    onCustomize?.();
  };

  return (
    <div>
      <div className="mb-[17px] md:hidden">
        <h1 className="font-primary mb-1.5 text-lg font-bold leading-snug text-black">
          {designName}
        </h1>
        {collectionName && collectionPageUrl && (
          <p className="mb-1.5 text-xs leading-snug text-black">
            From{' '}
            <AppLink
              to={collectionPageUrl}
              shouldRefresh={false}
              className="font-bold text-[var(--link-blue)] no-underline hover:underline"
            >
              {collectionName}
            </AppLink>
          </p>
        )}
        {shortDescLine ? (
          <p className="text-xs leading-normal text-[#999]">{shortDescLine}</p>
        ) : null}
      </div>

      <div className="pb-[7px] md:hidden">
        <div className="mb-[5px] flex w-full">
          <div className="w-20 shrink-0 self-center text-xs leading-snug text-black">
            {metalLabel}
          </div>
          <div className="flex-1 self-center">
            {goldPurity && <SpecPill uppercase>{goldPurity}</SpecPill>}
            {(goldPurity || metal) && <SpecPill>{metal}</SpecPill>}
            {!goldPurity && metalType && <SpecPill>{metalType}</SpecPill>}
            {metalWeight > 0 && <SpecPill>{formatWeight(metalWeight)}</SpecPill>}
          </div>
        </div>

        {stoneSpec && (
          <div className="mb-[5px] flex w-full">
            <div className="w-20 shrink-0 self-center text-xs leading-snug text-black">
              {stoneSpec.label}
            </div>
            <div className="flex-1 self-center">
              {stoneSpec.type && <SpecPill>{stoneSpec.type}</SpecPill>}
              {stoneSpec.carat != null && <SpecPill>{formatCarat(stoneSpec.carat)}</SpecPill>}
            </div>
          </div>
        )}
      </div>

      <div className="flex w-full border-t border-[#ededed] py-2 md:hidden">
        <div className={cn('text-center', showCustomization ? 'w-1/2' : 'w-full')}>
          <button
            type="button"
            className="inline-block cursor-pointer border-0 bg-transparent px-2 py-1.5 text-[13px] leading-snug text-[var(--link-blue)] hover:underline"
            onClick={handleViewMoreDetails}
          >
            View More Details
          </button>
        </div>
        {showCustomization && (
          <div className="relative w-1/2 text-center before:absolute before:left-0 before:top-1/2 before:h-[30px] before:w-px before:-translate-y-1/2 before:bg-[#ededed]">
            <button
              type="button"
              className="inline-block cursor-pointer border-0 bg-transparent px-2 py-1.5 text-[13px] leading-snug text-[var(--link-blue)] hover:underline"
              onClick={handleCustomizeClick}
            >
              Customize this&nbsp;Design
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
